import { ITelephonyConfiguration, TelephonyServiceProvider } from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { copyToClipboard } from '../../../../../models/UiUtils';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useReleasePhoneNumberMutation } from '../../../../../queries/PhoneNumber/useReleasePhoneNumberMutation';
import { useToggleOutboundCallingMutation } from '../../../../../queries/PhoneNumber/useToggleOutboundCallingMutation';
import { Button } from '../../../../../web/components-v2/Button';
import { Checkbox } from '../../../../../web/components-v2/Checkbox';
import { DeleteConfirmDialog } from '../../../../../web/components-v2/DeleteConfirmDialog';
import { Pill } from '../../../../../web/components-v2/Pill';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { DuplicateIcon } from '../../../../../web/components/svgs/icons/DuplicateIcon';
import { XIcon } from '../../../../../web/components/svgs/icons/XIcon';
import { styleSheet } from '../styles';

interface IProps {
	isLoading: boolean;
	onClickToAddNumbers?: () => void;
	phoneNumbersList: ITelephonyConfiguration[];
}

export const NumbersList: React.FC<IProps> = ({ isLoading, onClickToAddNumbers, phoneNumbersList }) => {
	const userSession = useUserSession();
	const [phoneNumbers, setPhoneNumbers] = useState<ITelephonyConfiguration[]>(phoneNumbersList || []);
	const [pendingNumberIds, setPendingNumberIds] = useState<ReadonlySet<string>>(new Set());
	const [promptNumberId, setPromptNumberId] = useState<string>(null);
	const [phoneNumberToRelease, setPhoneNumberToRelease] = useState<ITelephonyConfiguration | null>(null);
	const [isReleaseDialogOpen, setIsReleaseDialogOpen] = useState(false);

	const isAdmin = userSession.account.isAdmin;

	const releasePhoneNumberMutation = useReleasePhoneNumberMutation({
		onSuccess: () => {
			toast.success(`Phone number ${phoneNumberToRelease?.number} has been released`);
			setPhoneNumbers(prev => prev.filter(p => p.id !== phoneNumberToRelease?.id));
			setPhoneNumberToRelease(null);
			setIsReleaseDialogOpen(false);
		},
		onError: error => {
			toast.error(error.message || 'Failed to release phone number');
			setIsReleaseDialogOpen(false);
		},
	});

	const handleReleaseClick = (phoneNumber: ITelephonyConfiguration) => {
		setPhoneNumberToRelease(phoneNumber);
		setIsReleaseDialogOpen(true);
	};

	const handleReleaseConfirm = () => {
		if (phoneNumberToRelease) {
			releasePhoneNumberMutation.mutate({ phoneNumberId: phoneNumberToRelease.id });
		}
	};

	useEffect(() => {
		setPhoneNumbers(phoneNumbersList);
	}, [phoneNumbersList]);

	const toggleOutboundCallingMutation = useToggleOutboundCallingMutation({
		onError: (error, variables) => {
			console.error('Toggle outbound calling failed', variables.phoneNumberId, error);
			toast.error('Could not update outbound calling');
		},
		onMutate: ({ phoneNumberId }) => {
			setPromptNumberId(null);
			setPendingNumberIds(prev => new Set(prev).add(phoneNumberId));
		},
		onSettled: ({ phoneNumberId }) => {
			setPendingNumberIds(prev => {
				const next = new Set(prev);
				next.delete(phoneNumberId);
				return next;
			});
		},
		onSuccess: (updated, { phoneNumberId }) => {
			setPhoneNumbers(prev => prev.map(x => (x.id === phoneNumberId ? updated : x)));
			setPromptNumberId(phoneNumberId);
		},
	});

	const toggleOutboundCalling = (phoneNumberId: string) => {
		setPromptNumberId(null);
		const current = phoneNumbers.find(x => x.id === phoneNumberId);
		if (!current) {
			return;
		}
		toggleOutboundCallingMutation.mutate({
			outboundCallingDisabled: !current.outboundCallingDisabled,
			phoneNumberId,
		});
	};

	const onCopyPhoneNumber = (id: string) => () => {
		const success = copyToClipboard(id);

		if (success) {
			toast.success('Phone number copied to clipboard');
		} else {
			toast.error('Failed to copy to clipboard');
		}
	};

	const promptNumber = phoneNumbers?.find(x => x.id === promptNumberId);
	const showPrompt = promptNumber !== undefined && promptNumber.outboundCallingDisabled === true;
	const canReleasePromptNumber = isAdmin && promptNumber?.provider === TelephonyServiceProvider.Twilio;

	return (
		<div className={css(styleSheet.numbersContainer)}>
			{phoneNumbers?.map(phoneNumber => {
				const isNew = moment(moment()).diff(phoneNumber.creationDate, 'seconds') <= 60;
				const isDisabled = phoneNumber.outboundCallingDisabled === true;
				const isTwilio = phoneNumber.provider === TelephonyServiceProvider.Twilio;
				const canRelease = isAdmin && isDisabled && isTwilio;
				const releaseNumber = () => handleReleaseClick(phoneNumber);
				return (
					<div key={phoneNumber.id} className={`${css(styleSheet.card)} lead-rule`}>
						{isNew && (
							<div className={css(styleSheet.newPill)}>
								<Pill variant='label' color='blue' label='New' />
							</div>
						)}

						{pendingNumberIds.has(phoneNumber.id) ? (
							<div className={css(styleSheet.loadingRow)}>
								<LoadingSpinner />
							</div>
						) : (
							<>
								<div className={css(styleSheet.toggleCell)}>
									<Checkbox
										id={`${phoneNumber.id}-outbound-toggle`}
										aria-label={`Outbound calling for ${phoneNumber.number}`}
										checked={!isDisabled}
										onCheckedChange={() => toggleOutboundCalling(phoneNumber.id)}
									/>
								</div>
								<div className={css(styleSheet.numberCell)}>
									{phoneNumber.number}
									<Button
										variant='ghost'
										size='icon'
										aria-label='Copy phone number'
										onClick={onCopyPhoneNumber(phoneNumber.number)}
									>
										<DuplicateIcon fillColor='currentColor' />
									</Button>
								</div>
								<div className={css(styleSheet.callingCell)}>Outbound Calling</div>
								<div className={css(styleSheet.connectionCell)}>{phoneNumber.connectionState}</div>
								<div className={css(styleSheet.dateCell)}>
									Created: {moment(phoneNumber.creationDate).format('M/D/YY h:mm a')}
								</div>
								<div className={css(styleSheet.providerCell)}>{`${phoneNumber.provider} (${phoneNumber.orderId})`}</div>
								<div className={css(styleSheet.releaseCell)}>
									{canRelease && (
										<Button variant='destructive-secondary' size='sm' onClick={releaseNumber}>
											Release
										</Button>
									)}
								</div>
							</>
						)}
					</div>
				);
			})}
			{showPrompt && (
				// A card, not a components-v2 primitive: none fits a message with two actions.
				<div className={css(styleSheet.prompt)} role='status'>
					<div className={css(styleSheet.promptHeader)}>
						<div className={css(styleSheet.promptLines)}>
							<span>This number has been disabled.</span>
							<span>Would you like to add another number for this user?</span>
						</div>
						<Button variant='ghost' size='icon' aria-label='Close' onClick={() => setPromptNumberId(null)}>
							<XIcon />
						</Button>
					</div>
					<div className={css(styleSheet.promptActions)}>
						<Button size='sm' onClick={onClickToAddNumbers}>
							Add another number
						</Button>
						{canReleasePromptNumber && (
							<Button variant='destructive-secondary' size='sm' onClick={() => handleReleaseClick(promptNumber)}>
								Release
							</Button>
						)}
					</div>
				</div>
			)}
			{isLoading && (
				<div className={css(styleSheet.spinnerRow)}>
					<LoadingSpinner type='large' />
				</div>
			)}
			<DeleteConfirmDialog
				open={isReleaseDialogOpen}
				title={`Release phone number ${phoneNumberToRelease?.number}?`}
				description="This number will be released immediately and cannot be recovered. It will be returned to Twilio's pool and may be assigned to another customer."
				isDeleting={releasePhoneNumberMutation.isPending}
				confirmLabel='Release Number'
				cancelLabel='Cancel'
				onClose={() => setIsReleaseDialogOpen(false)}
				onConfirm={handleReleaseConfirm}
			/>
		</div>
	);
};
